import useSWR from 'swr';
import backendCommunicator from 'services/backendCommunicator';
import useIsMounted from 'hooks/useIsMounted';

import Alert from 'helpers/Alert';
import {FormSelectionEnum, SWRKeyEnum} from 'interfaces';
import {dateReader, tonsToKilos} from 'helpers/UnitsFormatHandler';
import {type PlanningRecommendHarvestApiRecommendHarvestsGetRequest} from 'client';
import {useEffect, useState} from 'react';

const DEBOUNCE_DELAY: number = 1400;

function useGetHarvestRecommendations({
  planId,
  siteId,
  date,
  biomass,
  sortBy,
  excludeWithProperties,
}: {
  planId?: string;
  siteId?: string;
  date?: Date;
  biomass?: number;
  sortBy: PlanningRecommendHarvestApiRecommendHarvestsGetRequest['sortBy'];
  excludeWithProperties?: string[];
}) {
  const isMounted = useIsMounted();
  async function fetchHarvestRecommendations([planId, sortBy, siteId, biomass, date]: [
    planId: string,
    sortBy: PlanningRecommendHarvestApiRecommendHarvestsGetRequest['sortBy'],
    siteId?: string | null,
    biomass?: number | null,
    date?: number | null,
    excludeWithProperties?: string[],
  ]) {
    const _site = siteId ?? undefined;
    const biomassInKilos = biomass ? tonsToKilos(biomass) : undefined;
    try {
      if (!date) {
        return null;
      }
      const _date = new Date(date);
      const response = await backendCommunicator.getPlanHarvestRecommendation(
        planId,
        sortBy,
        _date,
        _site,
        biomassInKilos,
        excludeWithProperties,
      );

      const harvestRecommendations = response.data.map((harvest) => {
        return {
          ...harvest,
          dateObject: dateReader(harvest.date),
          isLocked: undefined,
          cellActionType: FormSelectionEnum.harvest,
        };
      });
      return harvestRecommendations;
    } catch (error) {
      isMounted.current && Alert.error(error);
    }
  }

  const [fetchKey, setFetchKey] = useState<Array<string[] | string | number | undefined> | null>(null);
  useEffect(() => {
    if (!planId) {
      return;
    }

    if (window.timeoutId) {
      clearTimeout(window.timeoutId);
    }

    window.timeoutId = setTimeout(() => {
      setFetchKey([
        planId,
        sortBy,
        siteId,
        biomass,
        date?.getTime(),
        excludeWithProperties,
        SWRKeyEnum.GET_PLAN_HARVESTS_RECOMMENDATION,
      ]);
    }, DEBOUNCE_DELAY);
  }, [planId, siteId, date, biomass, sortBy, excludeWithProperties]);

  const {data: recommendedHarvests, isLoading} = useSWR(fetchKey, fetchHarvestRecommendations);

  return {
    recommendedHarvests,
    isLoading,
  };
}
export default useGetHarvestRecommendations;
